import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingBlock } from '@/components/ui/Loader';
import { ROLE_HOME, type Role } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';

export interface ProtectedRouteProps {
  /** Omit to allow any signed-in user. */
  allowedRoles?: Role[];
}

/** The whole screen, while the session is being restored. */
function FullPageLoader() {
  return <LoadingBlock fullPage label="Loading your session" />;
}

/**
 * Gate for every signed-in area.
 *
 * The `isBootstrapping` check matters: on a page reload the session is restored
 * asynchronously from the refresh cookie. Without it, an authenticated user
 * would be bounced to /login for a split second on every refresh.
 */
export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const { user, isAuthenticated, isBootstrapping } = useAuthStore();

  if (isBootstrapping) return <FullPageLoader />;

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Signed in, but this area belongs to another role — send them to their own.
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={ROLE_HOME[user.role]} replace />;
  }

  return <Outlet />;
}
